import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Palette, 
  Code2, 
  Wrench, 
  Search, 
  ShoppingCart, 
  Globe,
  BookOpen,
  LineChart,
  Shield,
  HelpCircle,
  Layout,
  Smartphone,
  Lock,
  Code,
  BarChart,
  Database,
  FileText
} from 'lucide-react';
import { articles } from '../../data/articles';

interface MegaMenuItem {
  title: string;
  description: string;
  path: string;
  icon?: React.ElementType;
}

interface MegaMenuSection {
  title: string;
  items: MegaMenuItem[];
}

interface MegaMenuProps {
  sections: MegaMenuSection[];
  onClose: () => void;
}

const getIcon = (title: string) => {
  const icons = {
    'Web Design': Palette,
    'Full-Stack Development': Code2,
    'Website Maintenance': Wrench,
    'Technical SEO': Search,
    'E-commerce Solutions': ShoppingCart,
    'Web Applications': Globe,
    'Web Design Tips': Layout,
    'Mobile-First Design': Smartphone,
    'Website Security': Lock,
    'JavaScript Frameworks': Code,
    'Performance Optimization': BarChart,
    'CMS Guide': Database,
    'Web Analytics': LineChart,
    'Web Accessibility': Shield,
    'Documentation': FileText,
    'FAQs': HelpCircle,
    'Design': Layout,
    'Development': Code,
    'Security': Lock,
    'SEO': Search,
    'Analytics': LineChart,
    'Performance': BarChart,
    'Accessibility': Shield,
    'E-commerce': ShoppingCart
  };

  return icons[title as keyof typeof icons] || BookOpen;
};

const MegaMenu: React.FC<MegaMenuProps> = ({ sections, onClose }) => {
  // If this is the Articles section, replace items with actual articles
  const processedSections = sections.map(section => {
    if (section.title === 'Articles') {
      // Split articles into two columns
      const articlesPerColumn = Math.ceil(articles.length / 2);
      const firstColumnArticles = articles.slice(0, articlesPerColumn);
      const secondColumnArticles = articles.slice(articlesPerColumn);

      return [
        {
          title: 'Latest Articles',
          items: firstColumnArticles.map(article => ({
            title: article.title,
            description: article.excerpt,
            path: `/advice/${article.slug}`,
            icon: getIcon(article.category)
          }))
        },
        {
          title: 'More Articles',
          items: secondColumnArticles.map(article => ({
            title: article.title,
            description: article.excerpt,
            path: `/advice/${article.slug}`,
            icon: getIcon(article.category)
          }))
        }
      ];
    }
    return [section];
  }).flat();

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className="absolute top-full left-1/2 -translate-x-1/2 bg-white shadow-2xl rounded-lg border border-gray-100 z-50 w-[600px] max-w-[calc(100vw-2rem)] mt-1"
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseLeave={onClose}
    >
      <div className="p-4">
        <div className="grid grid-cols-2 gap-4">
          {processedSections.map((section) => (
            <div key={section.title} className="space-y-3">
              <h3 className="text-sm font-bold text-primary border-b border-gray-200 pb-2">
                {section.title}
              </h3>
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = getIcon(item.title);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="block group"
                      onClick={onClose}
                    >
                      <div className="p-2 rounded-lg transition-colors duration-200 hover:bg-gray-50 group-hover:bg-primary/5">
                        <div className="flex items-start gap-2">
                          <div className="text-primary group-hover:text-primary-dark transition-colors flex-shrink-0 mt-1">
                            <Icon size={18} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="text-secondary font-semibold group-hover:text-primary transition-colors truncate text-sm">
                              {item.title}
                            </h4>
                            <p className="text-xs text-gray-600 mt-0.5 group-hover:text-gray-700 line-clamp-2">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default MegaMenu;